// DMS Activity Service - Real implementation to connect to TurfAI DMS
import { authService } from './authService';

export interface Activity {
  id: string;
  name: string;
  description: string;
  workflow_type: string;  
  status: string;
  version: string;
  workflow_definition: any;
  input_schema: any;
  output_schema: any;
  metadata?: any;
  owner?: {
    id: string;
    username: string;
    email: string;
  };
}

export interface ActivitySearchParams {
  name?: string;
  workflow_type?: string;
  status?: string;
  pagination?: {
    page?: number;
    pageSize?: number;
  };
}

export class DMSActivityService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_TURFAI_API_URL || 'http://localhost:1338/api';
  }

  async getAllWorkflows(): Promise<Activity[]> {
    try {
      const headers = authService.getAuthHeaders();
      
      // Get all workflows (system + user's own) - backend handles visibility filtering
      const url = `${this.baseUrl}/activities`;
      
      console.log('🔍 Getting system workflows with server-side filtering:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`DMS Activities API error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('📋 System workflows found:', result.data || result);
      return result.data || result;
      
    } catch (error) {
      console.error('Error getting system workflows:', error);
      throw new Error(`Failed to get system workflows: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getWorkflowById(id: string): Promise<Activity | null> {
    try {
      const headers = authService.getAuthHeaders();
      
      const response = await fetch(`${this.baseUrl}/activities/${id}`, {
        method: 'GET',
        headers
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`DMS Activities API error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
      
    } catch (error) {
      console.error('Error getting activity by ID:', error);
      throw new Error(`Failed to get activity: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findWorkflowByName(name: string): Promise<Activity | null> {
    try {
      const allWorkflows = await this.getAllWorkflows();
      
      console.log(`🎯 Looking for workflow with exact name: ${name}`);
      const workflow = allWorkflows.find(w => w.name === name);
      
      if (workflow) {
        console.log(`✅ Found workflow: ${workflow.id} - ${workflow.name}`);
        return workflow;
      } else {
        console.log(`❌ No workflow found with name: ${name}`);
        console.log('📋 Available workflow names:', allWorkflows.map(w => w.name));
        return null;
      }
      
    } catch (error) {
      console.error('Error finding workflow by name:', error);
      return null;
    }
  }

  // getAllWorkflows is now the main method defined above
}

// Export singleton instance
export const dmsActivityService = new DMSActivityService();